import { ColorNeutralGrey3 } from 'autogen/design-tokens/tokens';
import Image from 'components/generic/Image';
import LinkAction from 'components/shared/Routing/components/LinkAction/LinkAction';
import imageOverlayStyle from 'components/shared/style/imageOverlay';
import type { HTMLAttributes } from 'react';
import styled from 'styled-components';
import DiagonalLines from './DiagonalLines';
import { DisplayTypeSize, selectedStyling, swatchStateStyling } from './shared/styles';
import type { SwatchRendererType } from './shared/types';
import { useTranslation } from 'react-i18next';
import { format } from 'modules/helpers/strings';
import { VariantImageAltText } from 'autogen/translation-keys/trans-website-productvariantpicker';

type ImageSwatchProps = Pick<
    SwatchRendererType,
    'image' | 'unavailable' | 'isSelected' | 'size' | 'name' | 'linkAction'
> &
    HTMLAttributes<HTMLSpanElement>;

const Wrapper = styled.div<{ $isSelected: boolean }>`
    position: relative;
    display: block;
    width: ${DisplayTypeSize.Image.width};
    height: ${DisplayTypeSize.Image.height};
    border: 1px solid ${ColorNeutralGrey3};
    padding: 0;
    background: transparent;
    ${swatchStateStyling({ $size: 'lg' })}
    ${({ $isSelected }) => $isSelected && selectedStyling({ $size: 'lg' })}

    &:before {
        content: '';
        z-index: 1;
        ${imageOverlayStyle}
    }
`;

const LinkWrapper = styled(LinkAction)<{ $isSelected: boolean }>`
    position: relative;
    display: block;
    width: ${DisplayTypeSize.Image.width};
    height: ${DisplayTypeSize.Image.height};
    border: 1px solid ${ColorNeutralGrey3};
    ${swatchStateStyling({ $size: 'lg' })}
    ${({ $isSelected }) => $isSelected && selectedStyling({ $size: 'lg' })}

    &:before {
        content: '';
        z-index: 1;
        ${imageOverlayStyle}
    }
`;

const Img = styled(Image.WithScaling)`
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

export default function ImageSwatch({
    image,
    name,
    isSelected,
    unavailable,
    linkAction,
    onClick,
    size = 'lg',
    ...restProps
}: ImageSwatchProps) {
    const { t } = useTranslation();

    if (!image) return null;

    const content = (
        <>
            <Img
                src={image}
                sizes={Number.parseInt(DisplayTypeSize.Image.width)}
                alt={format(t(VariantImageAltText))(name) || ''}
                title={name}
                transformType="SquareScale"
                imageType="product"
            />
            {unavailable && <DiagonalLines size={size} />}
        </>
    );

    if (onClick) {
        return (
            <Wrapper aria-label={name} $isSelected={isSelected} onClick={onClick} as="button" {...restProps}>
                {content}
            </Wrapper>
        );
    }

    if (!linkAction) {
        return (
            <Wrapper aria-label={name} $isSelected={isSelected} {...restProps}>
                {content}
            </Wrapper>
        );
    }

    return (
        <LinkWrapper action={linkAction} aria-label={name} $isSelected={isSelected} {...restProps}>
            {content}
        </LinkWrapper>
    );
}
